<template>
  <div class="reader-info-tabs" :class="{ 'is-bare': bare }">
    <!-- bare (2026-10-30): inside the room's turned card, which is the whole
         About now. No tab bar (the card's quiet rows pick the tab), no
         description on the drop's panel (the card's summary has it), and the
         content flows in the card's own scroll. -->
    <div v-if="!bare" class="rd-tabs">
      <button :class="['rd-tab', { active: activeTab === 'drop' }]" @click="activeTab = 'drop'">About</button>
      <button :class="['rd-tab', { active: activeTab === 'deck' }]" @click="activeTab = 'deck'">Card Deck</button>
      <button :class="['rd-tab', { active: activeTab === 'shared' }]" @click="activeTab = 'shared'">Lessons</button>
    </div>

    <div class="rd-tab-content">
      <!-- About: drop, then description (expanded first visit / collapsed on return) -->
      <div v-if="activeTab === 'drop'" class="rd-tab-panel">
        <!-- Today's drop is being made right now (this Mystic was asleep). -->
        <div v-if="dropPending" class="drop-hero drop-hero--pending" role="status" aria-live="polite">
          <div class="drop-hero__frame drop-hero__frame--pending">
            <span class="drop-hero__spinner" aria-hidden="true">✦</span>
            <p class="drop-hero__pending-title">Today's drop is on its way</p>
            <p class="drop-hero__pending-text">{{ data?.name || 'This Mystic' }} is drawing it now. It usually takes a minute or two.</p>
          </div>
        </div>

        <!-- Drop — image hero with the affirmation overlaid; click to enlarge -->
        <figure
          v-else-if="drop"
          class="drop-hero"
          role="button"
          tabindex="0"
          :aria-label="`Enlarge ${drop.title || 'the drop'}`"
          @click="selectedDrop = drop"
          @keydown.enter.prevent="selectedDrop = drop"
          @keydown.space.prevent="selectedDrop = drop"
        >
          <!-- Drop name + date — top-left, ABOVE the image. -->
          <figcaption class="drop-hero__over">
            <span class="drop-hero__label">{{ drop.label }}</span>
            <span class="drop-hero__dot">·</span>
            <span class="drop-hero__date">{{ dropDateLabel }}</span>
          </figcaption>

          <div class="drop-hero__frame">
            <img v-if="dropImage" :src="dropImage" :alt="drop.title" class="drop-hero__img" />
            <div v-else class="drop-hero__noimg">✦</div>

            <span class="drop-hero__sheen" aria-hidden="true"></span>
            <span class="drop-hero__enlarge" aria-hidden="true">⤢ Enlarge</span>
            <!-- ✦ Keep (mystic-page 2026-10-29): one verb for images from others. -->
            <KeepButton class="drop-hero__keep" :keep="drop.keep || null" :kept="!!drop.kept" />

            <div class="drop-hero__caption">
              <h4 v-if="drop.title" class="drop-hero__title">{{ drop.title }}</h4>
              <p v-if="drop.teaser" class="drop-hero__text">{{ drop.teaser }}</p>
            </div>
          </div>
        </figure>

        <!-- What the Mystic says, at the column's full width. Who they are —
             born, created by, readings, chart, Follow — lives in the rail. -->
        <template v-if="data?.reader_description && !bare">
          <p class="rd-eyebrow" aria-hidden="true">In {{ data?.name || 'their' }}’s words</p>
          <p class="rd-about-desc-text">{{ data.reader_description }}</p>
        </template>
      </div>

      <!-- Card Deck -->
      <div v-if="activeTab === 'deck'" class="rd-tab-panel">
        <!-- Deck identity band: name + meta (left), rating (right). -->
        <div class="deck-header">
          <div class="deck-id">
            <h3 class="deck-id-name">✦ {{ deckMeta.name || 'Tarot Deck' }}</h3>
            <div class="deck-id-meta" v-if="deckCreatedLabel || deckMeta.by_name">
              <span v-if="deckCreatedLabel">Created {{ deckCreatedLabel }}</span>
              <template v-if="deckMeta.by_name">
                <span v-if="deckCreatedLabel" class="deck-meta-dot">·</span><span>by
                  <a v-if="deckMeta.by_sub_domain" class="deck-by-link" @click="openCreator">{{ deckMeta.by_name }}</a>
                  <template v-else>{{ deckMeta.by_name }}</template>
                </span>
              </template>
            </div>
          </div>
          <div class="deck-rate">
            <span class="deck-rate-avg" v-if="deckRating.count">★ {{ deckRating.avg }} <em>({{ deckRating.count }})</em></span>
            <span class="deck-rate-avg deck-rate-none" v-else>Not yet rated</span>
            <div class="deck-rate-stars">
              <button
                v-for="n in 5"
                :key="n"
                type="button"
                class="deck-star"
                :class="{ on: n <= (hoverStar || deckRating.my_rating) }"
                @click="setDeckRating(n)"
                @mouseenter="hoverStar = n"
                @mouseleave="hoverStar = 0"
                :aria-label="`Rate ${n} star${n === 1 ? '' : 's'}`"
              >★</button>
              <span class="deck-rate-label">{{ deckRating.my_rating ? 'Your rating' : 'Rate it' }}</span>
            </div>
          </div>
        </div>
        <div class="deck-filters">
          <button
            v-for="f in deckFilters"
            :key="f.key"
            :class="['deck-tab', { active: activeDeckFilter === f.key }]"
            @click="activeDeckFilter = f.key"
          >{{ f.label }}</button>
        </div>
        <p v-if="!filteredDeck.length" class="deck-empty">
          This deck is still being painted.
        </p>
        <div v-else class="deck-grid" ref="deckGridEl">
          <div
            v-for="card in filteredDeck"
            :key="card.name"
            class="deck-card"
            :data-card-name="card.name"
            @click="openCardInDetail(card)"
          >
            <img v-if="card.image_url" :src="card.image_url" :alt="card.card_name" class="deck-card-img" />
            <div class="deck-card-overlay">
              <span v-if="card.suit === 'MJ' && card.number != null" class="deck-card-number">{{ card.number }}</span>
              <span class="deck-card-symbolic">{{ card.card_name }}</span>
              <span class="deck-card-tarot">{{ card.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Lessons (community-simplify DESIGN.md §5): the readings shared with
           this Mystic, each its lesson in the one LessonCard; a tap opens the
           lesson by short_uuid (the rows used to link by a seeker name that
           lessons leave blank, so the tap did nothing). -->
      <div v-if="activeTab === 'shared'" class="rd-tab-panel">
        <div v-if="lessons.length" class="rd-lessons" data-testid="mystic-lessons">
          <LessonCard
            v-for="(item, i) in lessons"
            :key="item.short_uuid || i"
            class="rd-lesson"
            :lesson="item"
            interactive
            @open="emit('open-lesson', item.short_uuid)"
          />
        </div>
        <p v-else class="rd-empty">Be the first to share a reading with {{ data?.name }}</p>
      </div>

    </div>

    <!-- Deck cards open the global card-detail modal (art + meaning + symbolism). -->

    <!-- Enlarged drop modal — big image with the reading as a toggleable overlay.
         On the body: inside the turned card a fixed layer would be the card's. -->
    <Teleport to="body">
    <div v-if="selectedDrop" class="drop-modal" @click.self="selectedDrop = null">
      <!-- Affirmation drop (no image): a clean text card. -->
      <div v-if="!dropHeroImage" class="drop-modal__panel drop-modal__panel--text" role="dialog" aria-modal="true">
        <button class="drop-modal__close drop-modal__close--text" aria-label="Close" @click="selectedDrop = null">×</button>
        <div class="drop-card__over drop-modal__over">
          <span class="drop-card__label">{{ selectedDrop.label }}</span>
          <span class="drop-card__dot">·</span>
          <span class="drop-card__date">{{ dropDateLabel }}</span>
        </div>
        <h3 v-if="selectedDrop.title" class="drop-modal__title">{{ selectedDrop.title }}</h3>
        <p class="drop-modal__affirm">{{ selectedDrop.reading_text || selectedDrop.teaser }}</p>
      </div>

      <!-- Image-led drop (zodiac / natal / tarot-blend): big image + tap-to-reveal. -->
      <div v-else class="drop-modal__panel" role="dialog" aria-modal="true">
        <img
          :src="dropHeroImage"
          :alt="selectedDrop.title"
          class="drop-modal__img"
          @click="showDetails = !showDetails"
        />

        <div class="drop-modal__controls">
          <button class="drop-modal__toggle" @click="showDetails = !showDetails">
            {{ showDetails ? 'Hide reading' : 'Show reading' }}
          </button>
          <button class="drop-modal__close" aria-label="Close" @click="selectedDrop = null">×</button>
        </div>

        <transition name="ov">
          <div v-show="showDetails" class="drop-modal__overlay" @click="showDetails = false">
            <div class="drop-modal__scrim">
              <button class="drop-modal__cardhide" aria-label="Hide reading" @click.stop="showDetails = false">Hide reading</button>
              <div class="drop-card__over drop-modal__over">
                <span class="drop-card__label">{{ selectedDrop.label }}</span>
                <span class="drop-card__dot">·</span>
                <span class="drop-card__date">{{ dropDateLabel }}</span>
              </div>
              <div class="drop-modal__layout">
                <img
                  v-if="selectedDrop.tarot_image_url"
                  :src="selectedDrop.tarot_image_url"
                  :alt="selectedDrop.tarot_card"
                  class="drop-modal__cardimg"
                />
                <div class="drop-modal__textcol">
                  <h3 v-if="selectedDrop.title" class="drop-modal__title">{{ selectedDrop.title }}<span v-if="selectedDrop.tarot_card" class="drop-modal__title-card"> - {{ selectedDrop.tarot_symbolic_name || selectedDrop.tarot_card }} · {{ selectedDrop.tarot_card }} {{ selectedDrop.tarot_reversed ? 'Reversed' : 'Upright' }}</span></h3>
                  <p v-if="selectedDrop.reading_text" class="drop-modal__reading">{{ selectedDrop.reading_text }}</p>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script setup>
import KeepButton from '@/components/ui/KeepButton.vue'
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { requestAuth } from '@/utils/request-auth'
import { static_store, store } from '@/store'
import { openCardDetailFor, showCardDetail } from '@/utils/card-detail'
import { cardsWithArt } from '@/utils/gallery'
import { getDeckRating, rateDeck } from '@/api-service'
import LessonCard from '@/components/ui/share/LessonCard.vue'
import axios from 'axios'
import { clientToday } from '@/utils/general-utils'
import { waitForTodayDrop } from '@/utils/drop-pending'

const props = defineProps({
  data: { type: Object, default: null },
  initialTab: { type: String, default: 'drop' },
  // First contact with this reader — description starts expanded; on return
  // visits it collapses behind a "Description" disclosure.
  isFirstVisit: { type: Boolean, default: true },
  // Inside the room's turned card (2026-10-30): no tab bar, no description.
  bare: { type: Boolean, default: false },
})

const emit = defineEmits(['open-lesson', 'toggle-follow'])

// The Mystic's lessons: rows with a lesson and a short_uuid (API.md §5).
const lessons = computed(() => ((props.data && props.data.shared_readings) || []).filter((r) => r && r.short_uuid && (r.lesson || r.summary_title))
  .map((r) => (r.lesson ? r : { ...r, lesson: r.summary_title })))

const activeTab = ref(props.initialTab)
// Honor a changed initialTab even if the component is reused across opens
// (e.g. "View Gallery" lands directly on the Card Deck tab).
watch(() => props.initialTab, (t) => { if (t) activeTab.value = t })
const activeDeckFilter = ref('all')
const selectedDrop = ref(null)
const showDetails = ref(true)

// Reading overlay starts visible each time a drop modal opens.
watch(selectedDrop, (v) => { if (v) showDetails.value = true })

// Today's drop as it arrives, once a pending one lands (see drop-pending.js).
const arrivedDrop = ref(null)
const drop = computed(() => arrivedDrop.value || props.data?.today_drop || null)
const gaveUp = ref(false)
const dropPending = computed(() => !!props.data?.drop_pending && !arrivedDrop.value && !gaveUp.value)
let stopWaiting = null

function waitForDrop() {
  stopWaiting?.()
  arrivedDrop.value = null
  gaveUp.value = false
  const sub = props.data?.sub_domain
  if (!props.data?.drop_pending || !sub) return
  const today = clientToday()
  stopWaiting = waitForTodayDrop({
    today,
    fetchDetail: () => axios.get(`${static_store.apiUrl}/reader/detail/${sub}/`, { params: { date: today } })
      .then(r => r.data),
    onDrop: (d) => { arrivedDrop.value = d },
    // Still not there after ten minutes: show the latest drop we have.
    onGiveUp: () => { gaveUp.value = true },
  })
}
watch(() => [props.data?.sub_domain, props.data?.drop_pending], waitForDrop, { immediate: true })
onUnmounted(() => stopWaiting?.())

const dropImage = computed(() => {
  const d = drop.value
  return d ? (d.drop_image_url || d.tarot_image_url) : null
})
// The enlarged-modal hero image — null for affirmation drops (text-only).
const dropHeroImage = computed(() => {
  const d = selectedDrop.value
  return d ? (d.drop_image_url || d.tarot_image_url) : null
})

// A deck card opens the shared card-detail modal — the Mystic's art + meaning +
// symbolism (fetched), replacing the old plain enlarged-image lightbox.
function openCardInDetail(card) {
  if (!card) return
  const sub = props.data && props.data.sub_domain
  if (sub && card.name) openCardDetailFor({ reader: sub, card: card.name })
  else showCardDetail({ name: card.card_name || card.name, image_url: card.image_url })
}

// Keep the deck grid centered on whatever card the detail modal is showing, so
// closing it (or arriving via "View In Gallery") leaves that card centered.
const deckGridEl = ref(null)
let focusCardName = ''   // persists across modal open/close + async card loads
function escSel(s) { return (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/"/g, '\\"') }
function findScrollParent(el) {
  let n = el && el.parentElement
  while (n) {
    const oy = getComputedStyle(n).overflowY
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n
    n = n.parentElement
  }
  return null
}
function centerDeckCard(name, attempt = 0) {
  if (!name) return
  nextTick(() => {
    const grid = deckGridEl.value
    const el = grid && grid.querySelector(`[data-card-name="${escSel(name)}"]`)
    const sc = el && findScrollParent(el)
    if (!el || !sc) {
      // Deck art may still be loading — retry briefly.
      if (attempt < 10) setTimeout(() => centerDeckCard(name, attempt + 1), 80)
      return
    }
    const scRect = sc.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    sc.scrollTop += (elRect.top - scRect.top) - (sc.clientHeight - elRect.height) / 2
  })
}
// NOTE: the watches that drive centering are registered in onMounted (below),
// AFTER filteredDeck is declared — registering them here would TDZ-crash setup.

// ── Deck rating + identity (whole-deck) ───────────────────────────────
const deckRating = ref({ avg: null, count: 0, my_rating: 0, deck: { name: '', created: '', card_count: 0, by_name: '', by_sub_domain: '' } })
const hoverStar = ref(0)
const deckMeta = computed(() => deckRating.value.deck || {})
const deckCreatedLabel = computed(() => {
  const iso = deckMeta.value.created
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
})
function openCreator() {
  const sub = deckMeta.value.by_sub_domain
  if (sub) window.dispatchEvent(new CustomEvent('open-reader-from-modal', { detail: { subdomain: sub, via: 'creator' } }))
}
async function loadDeckRating() {
  const sub = props.data && props.data.sub_domain
  if (!sub) return
  const r = await getDeckRating(sub, store.session_id)
  if (r) deckRating.value = r
}
async function setDeckRating(n) {
  const sub = props.data && props.data.sub_domain
  if (!sub) return
  if (!store.querent_id) {
    requestAuth('mystics', { action: 'rate-deck', args: { subdomain: sub, stars: n } })
    return
  }
  const r = await rateDeck(sub, n, store.session_id)
  if (r) deckRating.value = r
}

function onKeydown(e) {
  if (e.key === 'Escape') {
    selectedDrop.value = null
  }
}
onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  // Seed from a modal that's ALREADY open at mount — the "View In Gallery" path
  // sets store.card_detail before this component mounts, so a non-immediate watch
  // would miss it. Center on that card once the deck tab + art are ready.
  if (store.card_detail && store.card_detail.name) {
    focusCardName = store.card_detail.name
    if (activeTab.value === 'deck') centerDeckCard(focusCardName)
  }
  // Center the deck on the card the modal is showing (open, tab switch, or once
  // the deck art finishes loading). Registered here so filteredDeck is defined.
  watch(() => store.card_detail, (cd) => {
    if (cd && cd.name) { focusCardName = cd.name; if (activeTab.value === 'deck') centerDeckCard(cd.name) }
  })
  watch(activeTab, (t) => {
    if (t === 'deck' && focusCardName) centerDeckCard(focusCardName)
    if (t === 'deck') loadDeckRating()
  })
  watch(() => filteredDeck.value.length, () => { if (activeTab.value === 'deck' && focusCardName) centerDeckCard(focusCardName) })
  if (activeTab.value === 'deck') loadDeckRating()
})
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

const deckFilters = [
  { key: 'all', label: 'All' },
  { key: 'MJ', label: 'Major' },
  { key: 'CP', label: 'Cups' },
  { key: 'WD', label: 'Wands' },
  { key: 'SW', label: 'Swords' },
  { key: 'PT', label: 'Pentacles' },
]

// Only painted cards. reader_detail returns all 78 ReaderDeck rows whether or
// not the Mystic has made art for them, and each art-less row still painted a
// bordered, empty box in the grid — a half-painted deck read as broken rather
// than as a deck in progress.
const filteredDeck = computed(() => {
  const painted = cardsWithArt(props.data?.deck)
  if (activeDeckFilter.value === 'all') return painted
  return painted.filter(c => c.suit === activeDeckFilter.value)
})

// Drop date — from the drop's own date when present, else today.
const dropDateLabel = computed(() => {
  const raw = drop.value?.drop_date
  const d = raw ? new Date(`${raw}T00:00:00`) : new Date()
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
})
</script>

<style scoped>
/* About {name}, redesigned (mystic room 2026-10-30, DESIGN.md §4): restyled
   only. The page scrolls as one; the tabs stick, Journal style; the drop sits
   on a cream caption like a lesson card; the Mystic's words carry the room's
   gold rule. Same events, same data. (The rules below this block are the
   built ones; these come later in the cascade where they differ.) */
.reader-info-tabs {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0 16px;
}

.rd-tabs {
  display: flex;
  gap: 0;
  /* The first tab's text starts on the panel content's left edge (18px panel
     padding − 14px tab padding), so the column has one spine. */
  padding: 0 4px;
  border-bottom: 1px solid rgba(195, 169, 255, 0.15);
  margin-bottom: 12px;
  flex-shrink: 0;
}
.rd-tab {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.35);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 8px 14px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
}
.rd-tab.active {
  color: #FFDFA3;
  border-bottom-color: #FFDFA3;
}
.rd-tab:hover {
  color: rgba(255, 255, 255, 0.6);
}

.rd-tab-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(102, 51, 153, 0.3) transparent;
}
/* Breathing room on the sides of every tab's content. */
.rd-tab-panel { padding: 4px 18px 14px; }
.rd-tab-content::-webkit-scrollbar { width: 4px; }
.rd-tab-content::-webkit-scrollbar-track { background: transparent; }
.rd-tab-content::-webkit-scrollbar-thumb { background: rgba(102, 51, 153, 0.3); border-radius: 2px; }

.rd-empty {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.25);
  font-style: italic;
  text-align: center;
  padding: 20px 0;
}

/* About — the description, below the drop at the column's full width. Upright,
   not italic: it is the Mystic's own words, not a caption. */
.rd-about-desc-text {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 18.5px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.55;
  margin: 22px 0 8px;
}
/* About — featured drop: drop name + date top-left ABOVE an image hero with the
   affirmation overlaid (mirrors the Studio Daily Affirmation hero). Click to enlarge. */
.drop-hero {
  display: block;
  margin: 8px 0 0;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}
.drop-hero:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; border-radius: 16px; }
.drop-hero:focus { outline: none; }

/* Eyebrow above the image — drop name + date, left-aligned. */
.drop-hero__over {
  display: flex; align-items: baseline; gap: 7px;
  margin: 0 0 8px; padding-left: 2px;
}
.drop-hero__label {
  font-family: 'Cinzel', serif; font-weight: 600; font-size: 12px;
  color: #FFDFA3; text-transform: uppercase; letter-spacing: 0.14em;
}
.drop-hero__dot { color: rgba(195, 169, 255, 0.45); }
.drop-hero__date {
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 13px; color: rgba(195, 169, 255, 0.7);
}

/* Image frame — the bordered, overlaid hero. */
.drop-hero__frame {
  position: relative;
  border: 1px solid rgba(255, 223, 163, 0.28);
  border-radius: 16px;
  overflow: hidden;
  background: #0a0015;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
  transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.4s, border-color 0.4s;
}
.drop-hero:hover .drop-hero__frame {
  transform: translateY(-3px);
  border-color: rgba(255, 223, 163, 0.55);
  box-shadow: 0 20px 46px rgba(0, 0, 0, 0.65), 0 0 26px rgba(255, 223, 163, 0.12);
}
.drop-hero__img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 7;
  max-height: 340px;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1);
}
.drop-hero:hover .drop-hero__img { transform: scale(1.04); }
/* Phones: a taller frame so the affirmation has room over the picture. */
@media (max-width: 699px) { .drop-hero__img { aspect-ratio: 4 / 3; } }
.drop-hero__frame--pending {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 240px; padding: 24px; text-align: center; gap: 8px;
}
.drop-hero__spinner {
  font-size: 40px; color: rgba(201, 169, 78, 0.8);
  animation: drop-pending-spin 2.4s linear infinite;
}
.drop-hero__pending-title { margin: 0; font-size: 1.05rem; color: rgba(255, 236, 200, 0.95); }
.drop-hero__pending-text { margin: 0; font-size: 0.9rem; color: rgba(255, 236, 200, 0.7); }
@keyframes drop-pending-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .drop-hero__spinner { animation: none; } }

.drop-hero__noimg {
  display: flex; align-items: center; justify-content: center;
  height: 240px; font-size: 44px; color: rgba(201, 169, 78, 0.4);
}

.drop-hero__sheen {
  position: absolute; inset: 0; z-index: 1;
  background: linear-gradient(115deg, transparent 32%, rgba(255, 223, 163, 0.12) 48%, transparent 64%);
  transform: translateX(-120%);
  transition: transform 0.75s ease;
  pointer-events: none;
}
.drop-hero:hover .drop-hero__sheen { transform: translateX(120%); }

/* Enlarge affordance — top-left pill (Keep holds the right), appears on hover. */
.drop-hero__enlarge {
  position: absolute; top: 12px; left: 12px; z-index: 3;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px;
  font-family: 'Cinzel', serif; font-size: 10.5px; letter-spacing: 0.08em;
  color: #0a0015; background: rgba(255, 223, 163, 0.92);
  border-radius: 999px;
  opacity: 0; transform: translateY(-4px);
  transition: opacity 0.3s, transform 0.3s;
  pointer-events: none;
}
.drop-hero:hover .drop-hero__enlarge,
.drop-hero:focus-visible .drop-hero__enlarge { opacity: 1; transform: translateY(0); }

/* ✦ Keep: the top-right corner of the drop. */
.drop-hero__keep { position: absolute; top: 12px; right: 12px; z-index: 4; }

/* Caption — label/date eyebrow + title + affirmation over a bottom scrim. */
.drop-hero__caption {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 2;
  padding: 44px 20px 18px;
  text-align: center;
  background: linear-gradient(to top, rgba(8, 2, 18, 0.93) 0%, rgba(8, 2, 18, 0.72) 45%, rgba(8, 2, 18, 0) 100%);
}
.drop-hero__title {
  font-family: 'Cinzel', serif; font-size: 17px; font-weight: 500;
  color: #FFE9BD; margin: 0 0 6px; line-height: 1.3;
}
.drop-hero__text {
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 20px; line-height: 1.45; color: #f6eed8;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.65);
}

/* Desktop two-column: the "Get Reading" drawer's top edge lands just below the
   left column's button. Pull the right-pane drop up and trim its image height a
   touch so it lines up with that button instead of being clobbered. */
@media (min-width: 700px) {
  .rd-tab-panel { padding-top: 0; }
  .drop-hero { margin-top: 2px; }
  .drop-hero__img { height: 300px; }
}

/* Label · date row — still used by the enlarged drop modal. */
.drop-card__over { display: flex; align-items: baseline; justify-content: center; gap: 8px; font-size: 11px; }
.drop-card__label {
  font-family: 'Cinzel', serif; font-weight: 600; color: #FFDFA3;
  text-transform: uppercase; letter-spacing: 0.14em;
}
.drop-card__dot { color: rgba(195, 169, 255, 0.4); }
.drop-card__date {
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 13px; color: rgba(195, 169, 255, 0.62);
}

/* Enlarged drop modal */
.drop-modal {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(6, 0, 14, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: dropFade 0.25s ease;
}
@keyframes dropFade { from { opacity: 0; } to { opacity: 1; } }
.drop-modal__panel {
  position: relative;
  display: inline-block;
  line-height: 0;
  background: #0a0015;
  border: 1px solid rgba(255, 223, 163, 0.25);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 40px 120px rgba(0, 0, 0, 0.75), 0 0 60px rgba(149, 108, 255, 0.12);
  animation: dropRise 0.38s cubic-bezier(0.2, 0.8, 0.2, 1);
}
/* Affirmation drop — text-only modal (no image). */
.drop-modal__panel--text {
  display: block; line-height: 1.6; max-width: 460px; width: calc(100vw - 48px);
  padding: 28px 30px 32px; text-align: center;
  background: radial-gradient(600px 300px at 50% 0%, #160a2e 0%, #0a0015 70%);
}
.drop-modal__close--text { position: absolute; top: 12px; right: 14px; background: rgba(0,0,0,0.35);
  border: none; color: #e9dcff; width: 30px; height: 30px; border-radius: 50%; font-size: 18px; cursor: pointer; }
.drop-modal__panel--text .drop-modal__over { justify-content: center; margin-bottom: 14px; }
.drop-modal__affirm { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 22px; line-height: 1.5; color: #f3e9d2; margin: 10px 0 0; text-shadow: 0 1px 10px rgba(0,0,0,0.5); }
@keyframes dropRise {
  from { opacity: 0; transform: translateY(18px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
.drop-modal__img {
  display: block;
  max-width: min(1080px, 95vw);
  max-height: 90vh;
  width: auto;
  height: auto;
  cursor: pointer;
}

/* Controls (toggle + close) */
.drop-modal__controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1;
}
.drop-modal__toggle {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #FFDFA3;
  background: rgba(10, 0, 21, 0.6);
  border: 1px solid rgba(255, 223, 163, 0.35);
  border-radius: 999px;
  padding: 7px 14px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  transition: background 0.2s, border-color 0.2s;
}
.drop-modal__toggle:hover {
  background: rgba(255, 223, 163, 0.16);
  border-color: rgba(255, 223, 163, 0.6);
}

/* Reading overlay layer */
.drop-modal__overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 82%;
  overflow-y: auto;
  padding: 40px 16px 16px;
  line-height: normal;
  cursor: pointer;
  background: linear-gradient(
    to top,
    rgba(7, 1, 14, 0.6) 0%,
    rgba(7, 1, 14, 0.28) 55%,
    rgba(7, 1, 14, 0) 100%
  );
  scrollbar-width: thin;
  scrollbar-color: rgba(102, 51, 153, 0.4) transparent;
}
.drop-modal__scrim {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 28px;
  border-radius: 14px;
  background: rgba(8, 2, 16, 0.52);
  border: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}
.drop-modal__cardhide {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 1;
  font-family: 'Cinzel', serif;
  font-size: 9px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: rgba(195, 169, 255, 0.65);
  background: rgba(10, 0, 21, 0.35);
  border: 1px solid rgba(195, 169, 255, 0.22);
  border-radius: 999px;
  padding: 4px 11px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}
.drop-modal__cardhide:hover {
  color: #FFDFA3;
  border-color: rgba(255, 223, 163, 0.5);
  background: rgba(255, 223, 163, 0.1);
}
.ov-enter-active,
.ov-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.ov-enter-from,
.ov-leave-to { opacity: 0; transform: translateY(16px); }
.drop-modal__over {
  font-size: 12px;
  align-self: flex-start;
  justify-content: flex-start;
  text-align: left;
}
.drop-modal__title {
  font-family: 'Cinzel', serif;
  font-size: 24px;
  font-weight: 500;
  color: #FFDFA3;
  line-height: 1.25;
  margin: 0;
}
.drop-modal__title-card {
  font-family: 'Cinzel', serif;
  font-size: 0.6em;
  font-weight: 400;
  letter-spacing: 0.02em;
  color: rgba(195, 169, 255, 0.72);
  white-space: nowrap;
}
.drop-modal__layout {
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 auto;
  text-align: left;
}
.drop-modal__cardimg {
  flex-shrink: 0;
  width: 104px;
  aspect-ratio: 2 / 3;
  object-fit: cover;
  border-radius: 9px;
  border: 1px solid rgba(255, 223, 163, 0.28);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.55);
}
.drop-modal__textcol {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}
.drop-modal__teaser {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 20px;
  font-style: italic;
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.5;
  margin: 0;
  max-width: 820px;
}
.drop-modal__reading {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 17px;
  color: rgba(255, 255, 255, 0.56);
  line-height: 1.65;
  margin: 0;
  max-width: 820px;
}
.drop-modal__close {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(10, 0, 21, 0.6);
  color: rgba(255, 255, 255, 0.7);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}
.drop-modal__close:hover {
  background: rgba(255, 223, 163, 0.15);
  color: #FFDFA3;
  border-color: rgba(255, 223, 163, 0.5);
}
@media (max-width: 640px) {
  .drop-modal__overlay { padding: 64px 20px 22px; }
  .drop-modal__title { font-size: 22px; }
  .drop-modal__teaser { font-size: 18px; }
}

/* Card Deck */
/* Deck identity band — name + meta (left), rating (right), on one baseline. */
.deck-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  padding: 0 0 14px; margin-bottom: 14px; border-bottom: 1px solid rgba(155,134,201,0.18); }
.deck-id { min-width: 0; }
.deck-id-name { font-family: 'Cinzel', serif; font-size: 20px; color: #FFDFA3; margin: 0 0 5px; letter-spacing: 0.02em; }
.deck-id-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; font-size: 12px; color: rgba(195,169,255,0.62); }
.deck-meta-dot { color: rgba(195,169,255,0.3); }
.deck-by-link { color: #FFDFA3; cursor: pointer; text-decoration: none; }
.deck-by-link:hover { text-decoration: underline; }
.deck-rate { display: flex; flex-direction: column; align-items: flex-end; gap: 5px; flex: none; }
.deck-rate-avg { font-family: 'Cinzel', serif; font-size: 16px; color: #FFDFA3; }
.deck-rate-avg em { font-style: normal; font-size: 11px; color: rgba(195,169,255,0.6); }
.deck-rate-none { font-size: 12px; color: rgba(195,169,255,0.5); }
.deck-rate-stars { display: inline-flex; align-items: center; gap: 2px; }
.deck-star { background: none; border: none; cursor: pointer; padding: 0 1px; font-size: 18px; line-height: 1;
  color: rgba(195,169,255,0.3); transition: color 0.12s ease; }
.deck-star.on, .deck-star:hover { color: #FFDFA3; }
.deck-rate-label { margin-left: 8px; font-size: 11px; color: rgba(195,169,255,0.6); }
.deck-filters {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.deck-tab {
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 12px;
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.15);
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  font-family: inherit;
}
.deck-tab.active {
  background: rgba(195, 169, 255, 0.15);
  color: #FFDFA3;
  border-color: rgba(195, 169, 255, 0.3);
}
/* Nothing painted yet — say so plainly rather than laying out empty slots. */
.deck-empty {
  margin: 18px 0;
  font-size: 13px;
  font-style: italic;
  color: rgba(240, 217, 163, 0.55);
}
.deck-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.deck-card {
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(240, 217, 163, 0.18);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.45);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}
.deck-card:hover {
  transform: translateY(-4px);
  border-color: rgba(240, 217, 163, 0.55);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.6), 0 0 22px rgba(255, 223, 163, 0.12);
}
.deck-card-img { width: 100%; height: auto; display: block; }
.deck-card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(10, 0, 21, 0.94) 0%, rgba(10, 0, 21, 0.5) 38%, transparent 72%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 1px;
  padding: 8px 6px;
}
.deck-card-number {
  position: absolute;
  top: 6px;
  left: 8px;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
}
/* Symbolic name leads; traditional name small beneath (matches the modal). */
.deck-card-symbolic {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 500;
  color: #FFDFA3;
  text-align: center;
  line-height: 1.2;
}
.deck-card-tarot {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-size: 9px;
  color: rgba(232, 216, 255, 0.6);
  text-align: center;
}

/* Lessons: one column in the narrow rail, two from 900px. */
.rd-lessons { display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
.rd-lessons > .rd-lesson { width: 100%; max-width: none; }
@media (min-width: 900px) { .rd-lessons { grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: stretch; } }

/* Card detail modal */
.image-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
  gap: 12px;
}
.image-modal-img {
  max-width: 80vw;
  max-height: 80vh;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8);
}
.image-modal-caption {
  font-family: 'Cinzel', serif;
  color: #FFDFA3;
  font-size: 14px;
}

/* ── About page restyle (2026-10-30) ── */
.reader-info-tabs { height: auto; padding: 0; display: block; }
.rd-tabs {
  position: sticky; top: 0; z-index: 8; justify-content: center; gap: 34px; margin: 26px 44px 0; padding: 14px 0 0;
  border-bottom: 1px solid rgba(240, 217, 163, 0.18);
  background: linear-gradient(180deg, rgba(11, 5, 24, 0.96), rgba(11, 5, 24, 0.9)); backdrop-filter: blur(6px);
}
.rd-tab {
  padding: 4px 4px 12px; margin-bottom: -1px; font-size: 14px; letter-spacing: 0.1em; color: #9c8bbd;
}
.rd-tab:hover { color: #d9cbf2; }
.rd-tab.active { color: #f6e7c1; border-bottom-color: #e9cf86; }
.rd-tab-content { overflow: visible; }
.rd-tab-panel { padding: 28px 44px 0; max-width: 940px; margin: 0 auto; box-sizing: border-box; }
.drop-hero__over { margin-bottom: 12px; }
.drop-hero__label { font-size: 12px; letter-spacing: 0.24em; color: #f0d9a3; }
.drop-hero__date { font-size: 16px; color: #b9a6dd; }
.drop-hero__frame { background: #f3ebdc; border-color: rgba(240, 217, 163, 0.4); box-shadow: 0 14px 34px -14px rgba(0, 0, 0, 0.8); }
.drop-hero__img { aspect-ratio: 3 / 2; height: auto; max-height: none; }
.drop-hero__caption { position: static; padding: 16px 20px 18px; text-align: left; background: #f3ebdc; }
.drop-hero__title { font-size: 18px; font-weight: 600; letter-spacing: 0.04em; color: #2a1845; margin: 0; }
.drop-hero__text { margin-top: 4px; font-size: 19px; line-height: 1.4; color: #3d2f55; text-shadow: none; }
.rd-eyebrow {
  margin: 34px 0 12px; font-family: 'Cinzel', serif; font-size: 12px; font-weight: 600; letter-spacing: 0.24em;
  text-transform: uppercase; color: #f0d9a3;
}
.rd-about-desc-text {
  position: relative; margin: 0; padding: 2px 0 2px 22px; max-width: 40em;
  font-size: 21px; line-height: 1.6; color: #efe6ff;
}
.rd-about-desc-text::before {
  content: ''; position: absolute; left: 0; top: 6px; bottom: 6px; width: 2px; border-radius: 2px;
  background: linear-gradient(180deg, #f0d9a3, rgba(240, 217, 163, 0.45) 38%, rgba(240, 217, 163, 0.05));
}
/* Bare, inside the turned card: the card's scroll, its width, one column. */
.reader-info-tabs.is-bare .rd-tab-content { overflow: visible; }
.reader-info-tabs.is-bare .rd-tab-panel { padding: 0; max-width: none; }
.reader-info-tabs.is-bare .rd-lessons { grid-template-columns: minmax(0, 1fr); }
.reader-info-tabs.is-bare .deck-header { margin-bottom: 12px; }
.reader-info-tabs.is-bare .deck-id-name { font-size: 17px; }
.reader-info-tabs.is-bare .deck-grid { gap: 9px; }
.reader-info-tabs.is-bare .drop-hero { margin-top: 0; }
.reader-info-tabs.is-bare .drop-hero__img { height: auto; }
@media (max-width: 860px) {
  .rd-tabs { margin: 22px 0 0; gap: 20px; justify-content: flex-start; padding-left: 16px; padding-right: 64px; }
  .rd-tab-panel { padding: 22px 16px 0; }
  .rd-about-desc-text { font-size: 19px; }
}
</style>
